import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { canEnterData } from '../lib/types'
import { leaseTouchesYear } from '../lib/rules'
import { useSelectedYear } from '../year/useYear'
import type { Tenant } from '../lib/types'
import { Loading, ErrorState, EmptyState } from '../components/States'
import { TenantSheet } from './TenantSheet'

interface TenantRow extends Tenant {
  /** Nombre total de baux, toutes annees confondues. */
  leaseCount: number
  leases: {
    unit: { name: string } | null
    status: string
    start_date: string | null
    end_date: string | null
  }[]
}

/**
 * Les locataires DE L'ANNEE CONSULTEE : ceux dont un bail concerne
 * cette annee. Ouvrir 2025 ne montre donc pas un locataire arrive en
 * 2026.
 *
 * AVEC UNE EXCEPTION : un locataire qui n'a AUCUN bail est montre
 * quelle que soit l'annee. Il n'appartient a aucune annee, donc le
 * filtre le ferait disparaitre de partout, et c'est justement celui
 * qu'on veut pouvoir corriger ou supprimer : une saisie faite par
 * erreur, ou une fiche creee avant que le bail ne soit signe.
 *
 * leaseCount compte TOUS ses baux, toutes annees confondues, et non
 * ceux de l'annee consultee : c'est ce nombre qui decide si le
 * locataire est supprimable, et un bail dans une autre annee compte
 * tout autant.
 */
async function loadTenants(year: number): Promise<TenantRow[]> {
  const { data, error } = await supabase
    .from('tenants')
    .select(
      'id, full_name, phone, id_type, id_number, notes,' +
        ' leases(status, start_date, end_date, unit:units(name))',
    )
    .order('full_name', { ascending: true })
  if (error) throw error

  return ((data ?? []) as unknown as TenantRow[])
    .map((tn) => ({
      ...tn,
      leaseCount: (tn.leases ?? []).length,
      leases: (tn.leases ?? []).filter((l) => leaseTouchesYear(l, year)),
    }))
    .filter((tn) => tn.leases.length > 0 || tn.leaseCount === 0)
}

export function TenantsScreen() {
  const { t } = useI18n()
  const { profile } = useAuth()
  const { year } = useSelectedYear()
  const { data, loading, error, reload } = useAsync(() => loadTenants(year), [year])
  const [sheet, setSheet] = useState<Tenant | null | 'new'>(null)

  const peutSaisir = canEnterData(profile?.role ?? null)

  return (
    <div className="screen">
      <div className="screen__head">
        <h1 className="screen__title">{t('tenants_title')}</h1>
        {peutSaisir && (
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={() => setSheet('new')}
          >
            {t('add')}
          </button>
        )}
      </div>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (data ?? []).length === 0 && (
        <EmptyState title={t('tenants_empty')} />
      )}

      {!loading && !error && (data ?? []).length > 0 && (
        <ul className="list">
          {(data ?? []).map((tn) => {
            // Unite occupee actuellement, si le locataire en a une.
            const courant = tn.leases?.find(
              (l) => l.status === 'actif' || l.status === 'expire',
            )
            return (
              <li key={tn.id}>
                <button
                  type="button"
                  className="list__item list__item--tappable"
                  onClick={() => peutSaisir && setSheet(tn)}
                >
                  <span className="list__main">
                    {/* Nom issu de la base, jamais traduit. */}
                    <span className="list__name">{tn.full_name}</span>
                    {tn.phone && <span className="list__meta">{tn.phone}</span>}
                    {tn.id_number && (
                      <span className="list__note">
                        {tn.id_type} {tn.id_number}
                      </span>
                    )}
                  </span>
                  <span className="list__side">
                    {courant?.unit && (
                      <span className="chip chip--ok">{courant.unit.name}</span>
                    )}
                    {/* Aucun bail : fiche creee par erreur, ou avant la
                        signature. C'est le seul cas supprimable. */}
                    {tn.leaseCount === 0 && (
                      <span className="chip chip--warn">{t('tenant_no_lease')}</span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {sheet !== null && (
        <TenantSheet
          tenant={sheet === "new" ? null : sheet}
          leaseCount={
            sheet === "new"
              ? 0
              : (data ?? []).find((x) => x.id === sheet.id)?.leaseCount ?? 0
          }
          onClose={() => setSheet(null)}
          onSaved={() => {
            setSheet(null)
            reload()
          }}
          onDeleted={() => {
            setSheet(null)
            reload()
          }}
        />
      )}
    </div>
  )
}
